import { Injectable } from '@angular/core';
import { LocalStorageService } from './storage/local-storage.service';
import { WebServiceService } from './web-service.service';
import { LogoutService } from './logout.service';
import { ToasterService } from 'angular2-toaster';

@Injectable()
export class TabTrackingService {
  private readonly TAB_IDS_KEY = 'active_tab_ids'; // Array of all active tab IDs in localStorage
  private readonly TAB_ID_KEY = 'tab_id'; // Current tab's unique ID in sessionStorage
  private tabId: string;
  private isInitialized: boolean = false;
  private listenersSetup: boolean = false;
  private monitorInterval: any = null;
  private cleanupInterval: any = null;
  private boundHandleStorageEvent: any = null;
  private boundHandleBeforeUnload: any = null;
  private isUnloading: boolean = false;

  constructor(
    private localStorageService: LocalStorageService,
    private webService: WebServiceService,
    private logoutService: LogoutService,
    private toasterService: ToasterService
  ) {
    // Check if tab ID already exists in sessionStorage (page refresh)
    this.tabId = this.getOrCreateTabId();
    // Set up listener to detect localStorage clearing (logout)
    this.setupLocalStorageMonitor();
    // Set up cleanup interval to remove stale tab IDs
    this.setupCleanupInterval();
    // Delay initialization to ensure Angular is fully loaded
    // Use setTimeout to run after Angular lifecycle
    setTimeout(() => {
      this.initializeTabTracking();
    }, 0);
  }

  /**
   * Monitor localStorage to detect when it's cleared (logout)
   */
  private setupLocalStorageMonitor(): void {
    // Clear any existing interval
    if (this.monitorInterval) {
      clearInterval(this.monitorInterval);
    }
    
    // Check periodically if tab_ids array exists (if user logged out, it will be cleared)
    // This handles the case where localStorage.clear() is called in the same tab
    this.monitorInterval = setInterval(() => {
      const tabIdsExist = localStorage.getItem(this.TAB_IDS_KEY) !== null;
      const userLoggedIn = this.isUserLoggedIn();
      
      // If user is logged in but tab_ids doesn't exist, reset and reinitialize
      if (userLoggedIn && !tabIdsExist && !localStorage.getItem('AutoLogout')) {

        this.reinitialize();
      }
      
      // If user is not logged in but we're initialized, reset state
      if (!userLoggedIn && this.isInitialized) {
        this.reset();
      }
    }, 1000); // Check every second
  }

  /**
   * Generate unique ID for this tab
   */
  private generateTabId(): string {
    return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get existing tab ID from sessionStorage or create a new one
   */
  private getOrCreateTabId(): string {
    try {
      const existingId = sessionStorage.getItem(this.TAB_ID_KEY);
      if (existingId) {
        return existingId;
      }
      // Generate new ID for new tab
      const newId = this.generateTabId();
      sessionStorage.setItem(this.TAB_ID_KEY, newId);
      return newId;
    } catch (error) {
      // If sessionStorage is not available, generate and use in-memory only
      console.warn('sessionStorage not available, using in-memory tab ID');
      return this.generateTabId();
    }
  }

  /**
   * Set up cleanup interval to remove stale tab IDs
   */
  private setupCleanupInterval(): void {
    // Clear any existing interval
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
    
    // Clean up stale tab IDs every 30 seconds
    // This handles cases where a tab closed unexpectedly without cleanup
    this.cleanupInterval = setInterval(() => {
      this.cleanupStaleTabIds();
    }, 30000);
  }

  /**
   * Initialize tab tracking
   */
  public initializeTabTracking(): void {
   

    // Only track tabs if user is logged in
    if (!this.isUserLoggedIn()) {
      // Reset initialization flag if user is not logged in (might have logged out)
      this.isInitialized = false;
      // Still set up listeners in case user logs in later
      this.setupEventListeners();
      return;
    }

    // Check if this tab ID is already in the active tabs array
    const activeTabIds = this.getActiveTabIds();
    const isTabAlreadyRegistered = activeTabIds.includes(this.tabId);

    if (this.isInitialized) {
      // If already initialized, check if tab is registered
      if (!isTabAlreadyRegistered) {
        this.isInitialized = false;
      } else {
        return;
      }
    }

    // Register this tab in the active tabs array
    this.registerTab();

    // Set up event listeners (only if not already set up)
    this.setupEventListeners();

    // Mark as initialized
    this.isInitialized = true;
  }

  /**
   * Check if we need to redirect to login (when app opens after all tabs were closed)
   */
 
  /**
   
  /**
   * Get a cookie value
   */


  /**
   * Set up event listeners for tab tracking
   */
  private setupEventListeners(): void {
    // Only set up listeners once
    if (this.listenersSetup) {
      return;
    }

    // Store bound handlers for cleanup
    this.boundHandleStorageEvent = this.handleStorageEvent.bind(this);
     this.boundHandleBeforeUnload = this.handleBeforeUnload.bind(this);
   
    // Listen for storage events (cross-tab communication)
    window.addEventListener('storage', this.boundHandleStorageEvent);

    // Listen for beforeunload event (tab close)
    window.addEventListener('beforeunload', this.boundHandleBeforeUnload);

    this.listenersSetup = true;
  }

  /**
   * Check if user is logged in
   */
  private isUserLoggedIn(): boolean {
    try {
      const userId = this.localStorageService.getUserId();
      return userId !== undefined && userId !== null && userId !== '';
    } catch (error) {
      return false;
    }
  }

  /**
   * Handle storage events from other tabs
   */
  private handleStorageEvent(event: StorageEvent): void {
    if (event.key === this.TAB_IDS_KEY) {
      // Another tab updated the active tabs list, sync if needed
      this.syncActiveTabs();
    }
    
    // Check if user logged in (checking for user_data or access_token changes)
    if ((event.key === 'user_data' || event.key === 'access_token') && event.newValue) {
      // User might have logged in, try to initialize if not already tracking
      if (!this.isInitialized || this.getTabCount() === 0) {
        setTimeout(() => {
          this.reinitialize();
        }, 100);
      }
    }

    // Check if localStorage was cleared (user logged out)
    // When localStorage.clear() is called, all keys are removed, so we check if tab_ids was removed
    if (event.key === this.TAB_IDS_KEY && event.newValue === null) {
      // Tab IDs array was removed, user might have logged out
      this.reset();
    }
  }

  /**
   * Handle beforeunload event (tab is closing or refreshing)
   */
  private handleBeforeUnload(event: BeforeUnloadEvent): void {
    // Mark that we're unloading to prevent duplicate processing
    if (!this.isUnloading) {
      this.isUnloading = true;
      // On refresh, the tab will re-register on next load since sessionStorage persists
      this.unregisterTab();
      
      // Check if we need to set AutoLogout flag (for browser close)
      this.handleBrowserClose();
    }
  }

 
  private handleBrowserClose(): void {
    try {
      // Only process if user is logged in
      if (!this.isUserLoggedIn()) {
        return;
      }

      // Check if strict auto logout policy is enabled
      // Only proceed with auto logout if enable_strict_auto_logout_policy is '1'
      const enableStrictAutoLogout = localStorage.getItem('enable_strict_auto_logout_policy');
      if (enableStrictAutoLogout !== '1') {
        // Strict auto logout is not enabled, don't set AutoLogout
        return;
      }

      // Check if this is the last tab by checking active tab IDs
      const activeTabIds = this.getActiveTabIds();
      const remainingTabs = activeTabIds.filter(id => id !== this.tabId);
      
      // If no other tabs remain, set AutoLogout flag
      if (remainingTabs.length === 0) {
        localStorage.setItem('AutoLogout', 'true');
        this.clearTabTrackingData();
      } else {
        // There are other tabs, so this is not the last tab - ensure AutoLogout is false
        localStorage.setItem('AutoLogout', 'false');
      }
    } catch (error) {
      // On error, check if strict auto logout is enabled before setting AutoLogout
      try {
        const enableStrictAutoLogout = localStorage.getItem('enable_strict_auto_logout_policy');
        if (enableStrictAutoLogout === '1') {
          localStorage.setItem('AutoLogout', 'true');
          this.clearTabTrackingData();
        }
      } catch (e) {
        // Ignore
      }
    }
  }

 
  private registerTab(): void {
    try {
      // Use a small delay to ensure localStorage is ready
      // This helps with race conditions when multiple tabs open simultaneously
      setTimeout(() => {
        const activeTabIds = this.getActiveTabIds();
        localStorage.setItem('AutoLogout', 'false');
        // Check if this tab is already registered (page refresh scenario)
        if (activeTabIds.includes(this.tabId)) {
          return;
        }
        
        // Add this tab ID to the active tabs array
        activeTabIds.push(this.tabId);
        localStorage.setItem(this.TAB_IDS_KEY, JSON.stringify(activeTabIds));
        sessionStorage.removeItem(this.TAB_ID_KEY);

      }, 50); // Small delay to avoid race conditions
    } catch (error) {
      console.error('Error registering tab:', error);
    }
  }

 
  private unregisterTab(): void {
    try {
      // Only process if user was logged in
      if (!this.isUserLoggedIn()) {
        return;
      }

      const activeTabIds = this.getActiveTabIds();
      const index = activeTabIds.indexOf(this.tabId);
      
      if (index > -1) {
        // Remove this tab's ID from the array
        activeTabIds.splice(index, 1);
        
        if (activeTabIds.length > 0) {
          localStorage.setItem(this.TAB_IDS_KEY, JSON.stringify(activeTabIds));
        } else {
          localStorage.removeItem(this.TAB_IDS_KEY);
        }

      } else {
      }
    } catch (error) {
    }
  }

  /**
   * Clean up stale tab IDs that are no longer active
   * This handles cases where tabs closed unexpectedly without cleanup
   */
  private cleanupStaleTabIds(): void {
    try {
      if (!this.isUserLoggedIn()) {
        return;
      }

      const activeTabIds = this.getActiveTabIds();
      const originalLength = activeTabIds.length;
      
       // However, we can remove duplicates if any
      const uniqueTabIds = Array.from(new Set(activeTabIds));
      if (uniqueTabIds.length !== originalLength) {
        localStorage.setItem(this.TAB_IDS_KEY, JSON.stringify(uniqueTabIds));
      }
      
      // Ensure current tab is in the list if user is logged in
      if (this.isUserLoggedIn() && !uniqueTabIds.includes(this.tabId)) {
        uniqueTabIds.push(this.tabId);
        localStorage.setItem(this.TAB_IDS_KEY, JSON.stringify(uniqueTabIds));
      }
    } catch (error) {
    }
  }

  /**
   * Get current tab count (based on active tab IDs array length)
   */
  private getTabCount(): number {
    return this.getActiveTabIds().length;
  }

  /**
   * Get list of active tab IDs from localStorage
   */
  private getActiveTabIds(): string[] {
    try {
      const tabIdsJson = localStorage.getItem(this.TAB_IDS_KEY);
      return tabIdsJson ? JSON.parse(tabIdsJson) : [];
    } catch (error) {
      console.error('Error getting active tab IDs:', error);
      return [];
    }
  }

  /**
   * Sync active tabs (helper method for cross-tab sync)
   */
  private syncActiveTabs(): void {
    const activeTabIds = this.getActiveTabIds();
  }

  private clearTabTrackingData(): void {
    try {
      localStorage.removeItem(this.TAB_IDS_KEY);
      // Note: We don't remove sessionStorage tab ID as it's per-tab and will be cleared when tab closes
    } catch (error) {
      console.error('Error clearing tab tracking data:', error);
    }
  }

  public getCurrentTabCount(): number {
    return this.getTabCount();
  }

  /**
   * Re-initialize tab tracking (useful if user logs in after service initialization)
   */
  public reinitialize(): void {
    // Reset initialization state to allow re-initialization
    this.isInitialized = false;
    // Call initialize which will check if user is logged in and set up tracking
    this.initializeTabTracking();
  }

  /**
   * Reset tab tracking state (call this when user logs out)
   */
  public reset(): void {
    this.isInitialized = false;
    this.isUnloading = false; // Reset unloading flag
    // Generate new tab ID for next session
    this.tabId = this.generateTabId();
    try {
      sessionStorage.setItem(this.TAB_ID_KEY, this.tabId);
    } catch (e) {
      // Ignore if sessionStorage not available
    }
    this.clearTabTrackingData();
    // Note: We keep listeners setup as they're still needed
    // The listenersSetup flag remains true
  }

  /**
   * Cleanup on service destroy
   */
  public cleanup(): void {
    // Clear monitoring intervals
    if (this.monitorInterval) {
      clearInterval(this.monitorInterval);
      this.monitorInterval = null;
    }
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }

    // Remove event listeners
    if (this.listenersSetup) {
      if (this.boundHandleStorageEvent) {
        window.removeEventListener('storage', this.boundHandleStorageEvent);
      }
      if (this.boundHandleBeforeUnload) {
        window.removeEventListener('beforeunload', this.boundHandleBeforeUnload);
      }
    
      this.listenersSetup = false;
    }
  }

  
}


// WEBPACK FOOTER //
// /codebuild/output/src3830577117/src/src/app/shared/services/tab-tracking.service.ts